import { useMemo, useState } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, Divider } from '@mui/material';
import { Plus, Search } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectFeatureGroupsTree, setFeatureGroupsTree } from 'store/dataReducers/rbacSlice';
import { FeatureGroupNode as FeatureGroupNodeType, Feature } from 'types/apis/rbacInterfaces';
import { FeatureGroupNode } from './FeatureGroupNode';
import { CreateFeatureGroupDialog } from './CreateFeatureGroupDialog';
import { AddFeaturesToGroupDialog } from './AddFeaturesToGroupDialog';
import { AddExistingSubgroupDialog } from './AddExistingSubgroupDialog';

interface FeatureGroupTreePanelProps {
  fullWidth?: boolean;
  /** Pre-filtered root groups to display — falls back to the full tree when omitted. */
  groups?: FeatureGroupNodeType[];
  /**
   * System (Blueprint) groups are auto-discovered from the backend — CRUD is
   * disabled here (design doc §2.2: "managed by backend auto-discovery").
   */
  readOnly?: boolean;
}

const collectGroupIds = (nodes: FeatureGroupNodeType[]): number[] =>
  nodes.flatMap((node) => [node.id, ...collectGroupIds(node.children || [])]);

/**
 * Keeps a group when its name/key matches, or when any descendant group or
 * contained feature matches. A matching group keeps ALL its features; a
 * non-matching group keeps only the features that match.
 */
const filterTree = (nodes: FeatureGroupNodeType[], term: string): FeatureGroupNodeType[] =>
  nodes
    .map((node): FeatureGroupNodeType | null => {
      const selfMatch = node.name.toLowerCase().includes(term) || node.key.toLowerCase().includes(term);
      const children = filterTree(node.children || [], term);
      const features = selfMatch
        ? node.features
        : (node.features || []).filter(
            (f) => (f.label || '').toLowerCase().includes(term) || f.endpoint.toLowerCase().includes(term),
          );
      if (!selfMatch && children.length === 0 && features.length === 0) return null;
      return { ...node, children, features };
    })
    .filter((node): node is FeatureGroupNodeType => node !== null);

export const FeatureGroupTreePanel = ({ fullWidth = false, groups, readOnly = false }: FeatureGroupTreePanelProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const dispatch = useDispatch();

  const fullFeatureGroupsTree = useSelector(selectFeatureGroupsTree);
  const featureGroupsTree = groups ?? fullFeatureGroupsTree;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [addFeaturesTarget, setAddFeaturesTarget] = useState<{ id: number; name: string } | null>(null);
  const [addSubgroupTarget, setAddSubgroupTarget] = useState<{ id: number; name: string } | null>(null);

  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;

  const visibleTree = useMemo(
    () => (searchActive ? filterTree(featureGroupsTree, searchTerm) : featureGroupsTree),
    [featureGroupsTree, searchTerm, searchActive],
  );

  const refetch = async () => {
    const resp = await RbacApis.getFeatureGroups(rc);
    if (resp.success && resp.data) {
      dispatch(setFeatureGroupsTree(resp.data));
    } else if (resp.error) {
      toast.error(resp.error);
    }
  };

  const handleToggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExpandAll = () => setExpanded(new Set(collectGroupIds(featureGroupsTree)));
  const handleCollapseAll = () => setExpanded(new Set());

  const handleCreate = async (body: { key: string; name: string; description?: string; parent_group_id?: number | null }) => {
    const resp = await RbacApis.createFeatureGroup(rc, body);
    if (resp.success) {
      toast.success('Feature group created');
      await refetch();
      if (body.parent_group_id != null) {
        setExpanded((prev) => new Set(prev).add(body.parent_group_id as number));
      }
      // Continue the flow: a new group is empty, so offer the feature picker right away.
      if (resp.data) {
        setAddFeaturesTarget({ id: resp.data.id, name: resp.data.name });
      }
    } else if (resp.error) {
      toast.error(resp.error);
    }
  };

  const handleDelete = async (node: FeatureGroupNodeType) => {
    const resp = await RbacApis.deleteFeatureGroup(rc, node.id, 'block');
    if (resp.success) {
      toast.success('Feature group deleted');
      await refetch();
    } else if (resp.error) {
      toast.error(resp.error);
    }
  };

  const handleAddFeatures = (node: FeatureGroupNodeType) => {
    if (readOnly) return;
    setAddFeaturesTarget({ id: node.id, name: node.name });
  };

  const handleAddSubgroup = (node: FeatureGroupNodeType) => {
    if (readOnly) return;
    setAddSubgroupTarget({ id: node.id, name: node.name });
  };

  const handleSubgroupAdded = async () => {
    await refetch();
    if (addSubgroupTarget) setExpanded((prev) => new Set(prev).add(addSubgroupTarget.id));
  };

  const handleRemoveFeature = async (feature: Feature, node: FeatureGroupNodeType) => {
    if (readOnly) return;
    const resp = await RbacApis.removeFeatureFromGroup(rc, node.id, feature.id);
    if (resp.success) {
      toast.success(`Removed “${feature.label || feature.endpoint}” from “${node.name}”`);
      await refetch();
    } else if (resp.error) {
      toast.error(resp.error);
    }
  };

  const handleDeleteGuarded = readOnly ? () => undefined : handleDelete;

  const emptyMessage = searchActive
    ? `No groups or permissions match "${search.trim()}".`
    : readOnly
      ? 'No system groups yet — they appear after backend route auto-discovery runs.'
      : 'No custom groups yet. Create one to bundle API permissions for assignment.';

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 360,
        maxWidth: fullWidth ? 760 : 360,
        borderRight: fullWidth ? 0 : 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          sx={{ flexGrow: 1 }}
          placeholder="Search groups & permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button size="small" onClick={handleExpandAll} disabled={searchActive} sx={{ whiteSpace: 'nowrap' }}>
          Expand all
        </Button>
        <Button size="small" onClick={handleCollapseAll} disabled={searchActive} sx={{ whiteSpace: 'nowrap' }}>
          Collapse all
        </Button>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Plus size={14} />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              setCreateParentId(null);
              setCreateOpen(true);
            }}
          >
            New Group
          </Button>
        )}
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        {visibleTree.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {emptyMessage}
          </Typography>
        )}
        {visibleTree.map((node) => (
          <FeatureGroupNode
            key={node.id}
            node={node}
            depth={0}
            expanded={expanded}
            forceExpand={searchActive}
            readOnly={readOnly}
            onToggleExpand={handleToggleExpand}
            onDelete={handleDeleteGuarded}
            onAddFeatures={handleAddFeatures}
            onAddSubgroup={handleAddSubgroup}
            onRemoveFeature={handleRemoveFeature}
          />
        ))}
      </Box>

      {!readOnly && (
        <>
          <CreateFeatureGroupDialog
            open={createOpen}
            defaultParentId={createParentId}
            onClose={() => {
              setCreateOpen(false);
              setCreateParentId(null);
            }}
            onCreate={handleCreate}
          />

          <AddFeaturesToGroupDialog
            open={addFeaturesTarget !== null}
            group={addFeaturesTarget}
            onClose={() => setAddFeaturesTarget(null)}
            onAdded={refetch}
          />

          <AddExistingSubgroupDialog
            open={addSubgroupTarget !== null}
            target={addSubgroupTarget}
            onClose={() => setAddSubgroupTarget(null)}
            onAdded={handleSubgroupAdded}
          />
        </>
      )}
    </Box>
  );
};
